import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getDramaBySlug, getRecommendedDramas, categories } from '@/mocks/dramas';

export default function DetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const drama = getDramaBySlug(slug);
  const recommended = getRecommendedDramas().filter((d) => d.slug !== slug).slice(0, 4);

  const [activeTab, setActiveTab] = useState<'episodes' | 'info'>('episodes');

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-18 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该剧集可能已下架或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === drama.category);

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
      { '@type': 'ListItem', position: 2, name: categoryInfo?.name || drama.category, item: typeof window !== 'undefined' ? `${window.location.origin}/category/${drama.category}` : '' },
      { '@type': 'ListItem', position: 3, name: drama.title },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${drama.title} - 久久影视`,
    description: drama.description,
    author: {
      '@type': 'Person',
      name: drama.director,
    },
    datePublished: `${drama.year}-01-01`,
    publisher: {
      '@type': 'Organization',
      name: '久久影视',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbList, articleSchema]) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div
          className="relative w-full h-[300px] md:h-[400px] bg-cover bg-center"
          style={{ backgroundImage: `url(${drama.bannerImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
        </div>

        <div className="w-full px-4 md:px-8 -mt-32 md:-mt-40 relative z-10">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-white/80 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-white transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${drama.category}`} className="hover:text-white transition-colors">
                {categoryInfo?.name || drama.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">{drama.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-40 md:w-56 shrink-0">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={drama.coverImage}
                    alt={`${drama.title} - ${drama.englishTitle}`}
                    title={`${drama.title} 电视剧全集 ${drama.englishTitle}`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 pt-2 md:pt-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                  {drama.title}
                </h1>
                <p className="text-sm text-white/60 mb-4">{drama.englishTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 text-sm rounded-lg bg-primary-500 text-white font-medium">
                    {drama.rating}
                  </span>
                  <span className="text-sm text-white/80">{drama.year}</span>
                  <span className="text-sm text-white/80">{drama.region}</span>
                  <span className="text-sm text-white/80">{drama.episodeCount}集</span>
                  <span className={`px-2 py-0.5 text-xs rounded-md font-medium ${
                    drama.status === '更新中' ? 'bg-accent-500 text-white' : 'bg-white/20 text-white'
                  }`}>
                    {drama.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {drama.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 text-xs rounded bg-white/15 text-white/80">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-white/70 leading-relaxed mb-4 max-w-2xl">
                  {drama.description}
                </p>

                <div className="grid grid-cols-2 gap-2 max-w-md">
                  <div className="text-xs text-white/60">
                    <span className="text-white/80 font-medium">导演：</span>{drama.director}
                  </div>
                  <div className="text-xs text-white/60 col-span-2">
                    <span className="text-white/80 font-medium">主演：</span>{drama.cast.join(' / ')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-1 p-1 rounded-xl bg-background-100 w-fit mb-8">
              <button
                onClick={() => setActiveTab('episodes')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'episodes'
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                选集列表 ({drama.episodes.length})
              </button>
              <button
                onClick={() => setActiveTab('info')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'info'
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                剧集信息
              </button>
            </div>

            {activeTab === 'episodes' && (
              <div>
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  选集列表
                </h2>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                  {drama.episodes.map((ep) => (
                    <button
                      key={ep.num}
                      className="h-10 rounded-lg bg-background-100 hover:bg-primary-500 hover:text-white text-foreground-700 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                      title={`${drama.title} ${ep.title}`}
                    >
                      {ep.num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'info' && (
              <div className="max-w-2xl">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                  剧集信息
                </h2>
                <div className="space-y-3 p-5 rounded-xl bg-background-100">
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">剧名：</span>
                    <span className="text-foreground-900 font-medium">{drama.title}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">英文名：</span>
                    <span className="text-foreground-700">{drama.englishTitle}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">导演：</span>
                    <span className="text-foreground-700">{drama.director}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">主演：</span>
                    <span className="text-foreground-700">{drama.cast.join('、')}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">类型：</span>
                    <span className="text-foreground-700">{drama.tags.join('、')}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">地区：</span>
                    <span className="text-foreground-700">{drama.region}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">年份：</span>
                    <span className="text-foreground-700">{drama.year}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">状态：</span>
                    <span className="text-foreground-700">{drama.status} · {drama.updateInfo}</span>
                  </div>
                  <div className="flex gap-3 text-sm">
                    <span className="text-foreground-500 w-16 shrink-0">简介：</span>
                    <span className="text-foreground-700 leading-relaxed">{drama.description}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {recommended.length > 0 && (
          <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {recommended.map((rec) => (
                  <Link
                    key={rec.slug}
                    to={`/detail/${rec.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={rec.coverImage}
                        alt={rec.title}
                        title={rec.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-primary-500 text-white">
                          {rec.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rec.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rec.updateInfo}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}